import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, ArrowRight, Search, X } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const TIER_ORDER = ["italian", "premium", "popular", "utility", "french", "japanese", "korean", "american", "other"];
export const TIER_LABELS = {
  italian: { it: "Italiane", en: "Italian" },
  premium: { it: "Premium", en: "Premium" },
  popular: { it: "Popolari", en: "Popular" },
  utility: { it: "Utility", en: "Utility" },
  french: { it: "Francesi", en: "French" },
  japanese: { it: "Giapponesi", en: "Japanese" },
  korean: { it: "Coreane", en: "Korean" },
  american: { it: "Americane", en: "American" },
  other: { it: "Altre", en: "Other" },
};

const norm = (s) => (s || "").toString().toLowerCase().trim();

export default function CatalogMenu({ linkCls }) {
  const { t, lang } = useLang();
  const [brands, setBrands] = useState([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeBrand, setActiveBrand] = useState(null); // {brand, products_count}
  const [modelsByBrand, setModelsByBrand] = useState({}); // brand -> [{model, count}]
  const [searchModels, setSearchModels] = useState([]); // global model matches for current query
  const [searchProducts, setSearchProducts] = useState([]); // global product matches
  const [modelsPanelHovered, setModelsPanelHovered] = useState(false); // BUG 2 lock
  const [anchorTop, setAnchorTop] = useState(96);
  const closeTimer = useRef(null);
  const searchDebounceRef = useRef(null);
  const searchRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    api.get("/vehicles/brands-summary")
      .then((r) => setBrands((r.data || []).filter((b) => b.products_count > 0)))
      .catch(() => setBrands([]));
  }, []);

  // Track the trigger position so the portalled menu sits right below it
  useEffect(() => {
    if (!open) return;
    const update = () => {
      const el = wrapRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setAnchorTop(Math.max(0, Math.round(rect.bottom + 10)));
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setActiveBrand(null);
      setSearchModels([]);
      setSearchProducts([]);
      setModelsPanelHovered(false);
    }
  }, [open]);

  // Debounced search: when the user types 2+ chars, fetch matching models + products
  // across ALL brands so the right panel becomes a full finder even when the search
  // term matches only a model or product (e.g. "Panda", "Golf") and no brand.
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    const q = norm(query);
    if (q.length < 2) { setSearchModels([]); setSearchProducts([]); return; }
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/catalog/search", { params: { q: query, limit: 12 } });
        setSearchModels(data?.models || []);
        setSearchProducts(data?.products || []);
      } catch {
        setSearchModels([]);
        setSearchProducts([]);
      }
    }, 220);
    return () => clearTimeout(searchDebounceRef.current);
  }, [query]);

  useEffect(() => {
    if (open && searchRef.current) {
      // small delay so the input is rendered before focus
      const id = setTimeout(() => searchRef.current?.focus(), 60);
      return () => clearTimeout(id);
    }
  }, [open]);

  const openNow = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const closeSoon = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  const filtered = useMemo(() => {
    const q = norm(query);
    if (!q) return brands;
    return brands.filter((b) => norm(b.brand).includes(q));
  }, [brands, query]);

  const groups = useMemo(() => (
    TIER_ORDER
      .map((tier) => ({
        tier,
        label: TIER_LABELS[tier]?.[lang] || TIER_LABELS[tier]?.it || tier,
        brands: filtered.filter((b) => b.tier === tier).sort((a, b) => a.brand.localeCompare(b.brand)),
      }))
      .filter((g) => g.brands.length > 0)
  ), [filtered, lang]);

  const loadModels = async (brandName) => {
    if (modelsByBrand[brandName]) return;
    try {
      const { data } = await api.get("/products", { params: { brand: brandName } });
      const counts = new Map();
      for (const p of data || []) {
        const m = (p.model || "").trim();
        if (!m) continue;
        counts.set(m, (counts.get(m) || 0) + 1);
      }
      const list = Array.from(counts.entries())
        .map(([model, count]) => ({ model, count }))
        .sort((a, b) => a.model.localeCompare(b.model));
      setModelsByBrand((prev) => ({ ...prev, [brandName]: list }));
    } catch {
      setModelsByBrand((prev) => ({ ...prev, [brandName]: [] }));
    }
  };

  const handleBrandHover = (b) => {
    // BUG 2 fix: while the user is inside the right models panel (scrolling the
    // models list, hovering a model link), IGNORE brand hover changes. Otherwise
    // the mouse briefly passing over another brand row while heading down to a
    // deep model in the panel would swap the panel content and make the menu
    // feel unstable / close. Also debounce very quick sequential hovers.
    if (modelsPanelHovered) return;
    setActiveBrand(b);
    loadModels(b.brand);
  };

  const activeModels = activeBrand ? modelsByBrand[activeBrand.brand] : null;

  return (
    <div
      ref={wrapRef}
      className="relative"
      onMouseEnter={openNow}
      onMouseLeave={closeSoon}
      onFocus={openNow}
      onBlur={closeSoon}
    >
      <Link
        to="/catalogo"
        data-testid="nav-catalog"
        className={`${linkCls({ isActive: false })} inline-flex items-center gap-1`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {t("nav_catalog")}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </Link>

      {open && brands.length > 0 && createPortal((
        <div
          data-testid="catalog-mega-menu"
          onMouseEnter={openNow}
          onMouseLeave={closeSoon}
          className="fixed left-1/2 -translate-x-1/2 z-[90] px-4"
          style={{ top: `${anchorTop}px` }}
        >
          <div className="w-[min(94vw,1080px)] rounded-2xl bg-xd-dark border border-xd-cyan/25 shadow-2xl shadow-black/40 p-6 backdrop-blur-md">
            {/* Header row: title + search + full catalog */}
            <div className="flex items-center justify-between gap-4 mb-4">
              <div>
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-cyan">
                  {lang === "en" ? "Shop by brand" : "Sfoglia per marca"}
                </div>
                <div className="font-display text-lg font-semibold text-white mt-0.5">
                  {lang === "en" ? "All car brands" : "Tutte le marche auto"}
                </div>
              </div>
              <Link
                to="/catalogo"
                data-testid="catalog-menu-see-all"
                onClick={() => setOpen(false)}
                className="text-xs font-medium text-xd-cyan hover:text-white inline-flex items-center gap-1 border border-xd-cyan/40 hover:border-xd-cyan rounded-lg px-3 py-1.5 transition-colors whitespace-nowrap"
              >
                {lang === "en" ? "Full catalog" : "Catalogo completo"} <ArrowRight className="w-3 h-3" />
              </Link>
            </div>

            <div className="relative mb-4">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="catalog-menu-search"
                placeholder={lang === "en" ? "Search brand or model (e.g. Fiat, Panda, Golf)…" : "Cerca marca o modello (es. Fiat, Panda, Golf)…"}
                className="w-full bg-xd-dark2 border border-xd-darkBorder text-white placeholder:text-slate-500 rounded-lg pl-9 pr-9 py-2.5 text-sm focus:outline-none focus:border-xd-cyan focus:ring-1 focus:ring-xd-cyan/50"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  data-testid="catalog-menu-search-clear"
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-md text-slate-400 hover:text-white hover:bg-xd-darkBorder flex items-center justify-center"
                  aria-label="Clear"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <div className="grid grid-cols-[1fr_260px] gap-6 min-h-[320px]">
              {/* Brands panel */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-5 gap-y-5 max-h-[60vh] overflow-y-auto pr-1">
                {groups.length === 0 ? (
                  <div className="col-span-full text-sm text-slate-400 py-8 text-center" data-testid="catalog-menu-no-brand-match">
                    {query && (searchModels.length > 0 || searchProducts.length > 0) ? (
                      <>
                        <div className="text-slate-500 text-xs">
                          {lang === "en" ? "No brand matches." : "Nessuna marca corrisponde."}
                        </div>
                        <div className="text-xd-cyan text-sm mt-2 font-medium">
                          {lang === "en" ? "→ See matching models on the right" : "→ Vedi i modelli trovati a destra"}
                        </div>
                      </>
                    ) : (
                      lang === "en" ? "No brand matches your search." : "Nessuna marca corrisponde alla ricerca."
                    )}
                  </div>
                ) : groups.map((g) => (
                  <div key={g.tier}>
                    <div className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-orange mb-2">
                      {g.label}
                    </div>
                    <ul className="space-y-0.5">
                      {g.brands.map((b) => {
                        const active = activeBrand?.brand === b.brand;
                        return (
                          <li key={b.brand}>
                            <div
                              onMouseEnter={() => handleBrandHover(b)}
                              onFocus={() => handleBrandHover(b)}
                              className={`flex items-center justify-between rounded-md px-2 py-1 transition-colors ${active ? "bg-xd-cyan/10" : "hover:bg-xd-dark2"}`}
                            >
                              <Link
                                to={`/marca/${encodeURIComponent(b.brand.toLowerCase().replace(/\s+/g, '-'))}`}
                                data-testid={`catalog-menu-brand-${b.brand}`}
                                onClick={() => setOpen(false)}
                                className={`flex-1 text-sm ${active ? "text-xd-cyan" : "text-slate-200 hover:text-white"} font-medium`}
                              >
                                {b.brand}
                              </Link>
                              <span className={`font-mono-tech text-[10px] ${active ? "text-xd-cyan/80" : "text-slate-500"}`}>{b.products_count}</span>
                              <ChevronRight className={`w-3.5 h-3.5 ml-1 ${active ? "text-xd-cyan" : "text-slate-600"}`} />
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>

              {/* Models side panel */}
              <aside
                data-testid="catalog-menu-models-panel"
                onMouseEnter={() => setModelsPanelHovered(true)}
                onMouseLeave={() => setModelsPanelHovered(false)}
                className="border-l border-xd-darkBorder pl-6"
              >
                {query && (searchModels.length > 0 || searchProducts.length > 0) ? (
                  <>
                    <div className="flex items-baseline justify-between mb-3">
                      <div>
                        <div className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-cyan">
                          {lang === "en" ? "Matching models" : "Modelli trovati"}
                        </div>
                        <div className="font-display text-white font-semibold mt-0.5">&quot;{query}&quot;</div>
                      </div>
                      <Link
                        to={`/catalogo?search=${encodeURIComponent(query)}`}
                        onClick={() => setOpen(false)}
                        data-testid="catalog-menu-search-all"
                        className="text-[11px] font-medium text-xd-cyan hover:text-white whitespace-nowrap"
                      >
                        {lang === "en" ? "See all" : "Vedi tutti"} →
                      </Link>
                    </div>
                    <ul className="space-y-0.5 max-h-[52vh] overflow-y-auto pr-1">
                      {searchModels.map((m) => (
                        <li key={`${m.brand}-${m.model}`}>
                          <Link
                            to={`/catalogo?brand=${encodeURIComponent(m.brand)}&model=${encodeURIComponent(m.model)}`}
                            onClick={() => setOpen(false)}
                            data-testid={`catalog-menu-search-model-${m.brand}-${m.model}`}
                            className="flex items-center justify-between rounded-md px-2 py-1 text-sm text-slate-200 hover:text-xd-cyan hover:bg-xd-dark2 transition-colors"
                          >
                            <span className="flex flex-col">
                              <span>{m.model}</span>
                              <span className="text-[10px] font-mono-tech text-slate-500">{m.brand}</span>
                            </span>
                            <span className="font-mono-tech text-[10px] text-slate-500">{m.count}</span>
                          </Link>
                        </li>
                      ))}
                      {searchProducts.length > 0 && (
                        <>
                          <li className="pt-3 pb-1 font-mono-tech text-[10px] uppercase tracking-widest text-xd-orange">
                            {lang === "en" ? "Products" : "Prodotti"}
                          </li>
                          {searchProducts.slice(0, 6).map((p) => (
                            <li key={p.id}>
                              <Link
                                to={`/prodotto/${p.id}`}
                                onClick={() => setOpen(false)}
                                data-testid={`catalog-menu-search-product-${p.id}`}
                                className="flex items-center justify-between rounded-md px-2 py-1 text-xs text-slate-200 hover:text-xd-cyan hover:bg-xd-dark2 transition-colors"
                              >
                                <span className="truncate max-w-[180px]">{p.name}</span>
                                <span className="font-mono-tech text-[10px] text-slate-500 whitespace-nowrap">€ {Number(p.price || 0).toFixed(0)}</span>
                              </Link>
                            </li>
                          ))}
                        </>
                      )}
                    </ul>
                  </>
                ) : query && searchModels.length === 0 && searchProducts.length === 0 ? (
                  <div className="text-xs text-slate-500 pt-2">
                    <div className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-cyan mb-2">
                      {lang === "en" ? "Models" : "Modelli"}
                    </div>
                    {lang === "en" ? "No model matches your search." : "Nessun modello corrisponde alla ricerca."}
                  </div>
                ) : !activeBrand ? (
                  <div className="text-xs text-slate-400 leading-relaxed pt-2">
                    <div className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-cyan mb-2">
                      {lang === "en" ? "Models" : "Modelli"}
                    </div>
                    {lang === "en"
                      ? "Hover a brand to see the available models, or type a model in the search."
                      : "Passa il mouse su una marca per vedere i modelli, oppure scrivi il modello nella ricerca."}
                  </div>
                ) : (
                  <>
                    <div className="flex items-baseline justify-between mb-3">
                      <div>
                        <div className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-cyan">
                          {lang === "en" ? "Models" : "Modelli"}
                        </div>
                        <div className="font-display text-white font-semibold mt-0.5">{activeBrand.brand}</div>
                      </div>
                      <Link
                        to={`/marca/${encodeURIComponent(activeBrand.brand.toLowerCase().replace(/\s+/g, '-'))}`}
                        onClick={() => setOpen(false)}
                        data-testid="catalog-menu-brand-all"
                        className="text-[11px] font-medium text-xd-cyan hover:text-white whitespace-nowrap"
                      >
                        {lang === "en" ? "See all" : "Vedi tutti"} →
                      </Link>
                    </div>
                    {activeModels === undefined || activeModels === null ? (
                      <div className="space-y-2">
                        {[0, 1, 2, 3].map((i) => (
                          <div key={i} className="h-6 rounded bg-xd-dark2 animate-pulse" />
                        ))}
                      </div>
                    ) : activeModels.length === 0 ? (
                      <div className="text-xs text-slate-500">
                        {lang === "en" ? "No models yet." : "Nessun modello disponibile."}
                      </div>
                    ) : (
                      <ul className="space-y-0.5 max-h-[52vh] overflow-y-auto pr-1">
                        {activeModels.map((m) => (
                          <li key={m.model}>
                            <Link
                              to={`/catalogo?brand=${encodeURIComponent(activeBrand.brand)}&model=${encodeURIComponent(m.model)}`}
                              onClick={() => setOpen(false)}
                              data-testid={`catalog-menu-model-${m.model}`}
                              className="flex items-center justify-between rounded-md px-2 py-1 text-sm text-slate-200 hover:text-xd-cyan hover:bg-xd-dark2 transition-colors"
                            >
                              <span>{m.model}</span>
                              <span className="font-mono-tech text-[10px] text-slate-500">{m.count}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </aside>
            </div>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}
